/* eslint-disable max-lines */
import { ThunkAction, ThunkDispatch } from "redux-thunk";

import { loadAndTrack } from "redux-request-loading";

import { questionnaireLoaded, saveQuestionnaireSuccess, submitQuestionnaireSuccess, SUBMIT_QUESTIONNAIRE } from "@teamprevent/common/questionnaires/actions";
import { AnsweredQuestionWithId } from "@teamprevent/common/model";
import { MedicalTypeId } from "@teamprevent/common/healthSurveillance/models";

import axios from "axios";

import { Payload } from "@neworbit/redux-helpers";
import { HealthSurveillanceQuestionnaireKey } from "@teamprevent/common/healthSurveillance";

import { OhCaseAction, actionTaskSuccess, loadOhCaseDetailSuccess } from "../crud/ohCase/actions";
import { ohCaseApi } from "../crud/ohCase";
import { ApplicationState } from "../applicationState";
import { OhCaseDetailModel } from "../crud/ohCase/model";

import { Dispatch } from "../store";

import { HealthSurveillanceApi } from "./healthSurveillanceApi";

import {
    HealthSurveillanceCaseCreateModel,
    ClinicHealthSurveillanceQuestionnaire,
    LinkedHealthSurveillanceCaseCreateModel,
    HealthSurveillanceQuestionnaireAutoSaveDto,
    SubmitClinicHealthSurveillanceQuestionnaireResponseDto
} from "./models";

import {
    CREATE_HEALTH_SURVEILLANCE_CASE,
    CREATE_HEALTH_SURVEILLANCE_CASE_SUCCESS,
    LOAD_HEALTH_SURVEILLANCE_QUESTIONNAIRE,
    LOADED_CLINIC_HEALTH_SURVEILLANCE_QUESTIONNAIRE,
    SUBMIT_CLINIC_HEALTH_SURVEILLANCE_QUESTIONNAIRE_SUCCESS,
    SUBMIT_CLINIC_HEALTH_SURVEILLANCE_CONSULTATION_SUCCESS,
    LOAD_HEALTH_SURVEILLANCE_CONSULTATION,
    SEND_HEALTH_SURVEILLANCE_QUESTIONNAIRE,
    COMPLETE_NOW_HEALTH_SURVEILLANCE_QUESTIONNAIRE,
    ACTION_FURTHER_INFORMATION_AND_REVIEW_OF_FITNESS,
    DELETE_HEALTH_SURVEILLANCE_QUESTIONNAIRE_EVIDENCE,
    DELETE_HEALTH_SURVEILLANCE_CONSULTATION_EVIDENCE,
    LOAD_HEALTH_SURVEILLANCE_REPEAT_MEDICAL_CONSULTATION,
    SUBMIT_CLINIC_HEALTH_SURVEILLANCE_REPEAT_CONSULTATION_SUCCESS
} from "./actionTypes";

import { HealthSurveillanceConsultationKey, HealthSurveillanceRepeatMedicalConsultationKey } from "./components/HealthSurveillanceConsultation";
import { HavsClinicalAssessmentOfHistoryAndExaminationKey } from "./components/HavsClinicalAssessmentOfHistoryAndExaminationQuestions";

type ClinicHealthSurveillanceQuestionnaireLoadedAction =
    Payload<ClinicHealthSurveillanceQuestionnaire> & { type: typeof LOADED_CLINIC_HEALTH_SURVEILLANCE_QUESTIONNAIRE };
type SubmitHealthSurveillanceQuestionnaireSuccessAction =
    Payload<ClinicHealthSurveillanceQuestionnaire> & { type: typeof SUBMIT_CLINIC_HEALTH_SURVEILLANCE_QUESTIONNAIRE_SUCCESS };
type SubmitHealthSurveillanceConsultationSuccessAction =
    Payload<{ clinicId: string; appointmentId: string }> & { type: typeof SUBMIT_CLINIC_HEALTH_SURVEILLANCE_CONSULTATION_SUCCESS };
type SubmitHealthSurveillanceRepeatConsultationSuccessAction =
    Payload<{ clinicId: string; appointmentId: string }> & { type: typeof SUBMIT_CLINIC_HEALTH_SURVEILLANCE_REPEAT_CONSULTATION_SUCCESS };
export type HealthSurveillanceAction =
    ClinicHealthSurveillanceQuestionnaireLoadedAction |
    SubmitHealthSurveillanceQuestionnaireSuccessAction |
    SubmitHealthSurveillanceConsultationSuccessAction |
    SubmitHealthSurveillanceRepeatConsultationSuccessAction;

export const createHealthSurveillanceCase = (customerId: string, employeeId: string, healthSurveillanceCase: HealthSurveillanceCaseCreateModel):
    ThunkAction<Promise<OhCaseDetailModel>, ApplicationState, undefined, any> => {
    return async (dispatch: ThunkDispatch<ApplicationState, undefined, any>) => {
        const result = await loadAndTrack(
            dispatch,
            CREATE_HEALTH_SURVEILLANCE_CASE,
            ohCaseApi.createHealthSurveillanceCase(customerId, employeeId, healthSurveillanceCase));
        dispatch(createHealthSurveillanceCaseSuccess(result));
        return result;
    };
};

export const createLinkedHealthSurveillanceCase = (customerId: string, employeeId: string, healthSurveillanceCase: LinkedHealthSurveillanceCaseCreateModel):
    ThunkAction<Promise<OhCaseDetailModel>, ApplicationState, undefined, any> => {
    return async (dispatch: ThunkDispatch<ApplicationState, undefined, any>) => {
        const result = await loadAndTrack(
            dispatch,
            CREATE_HEALTH_SURVEILLANCE_CASE,
            ohCaseApi.createLinkedHealthSurveillanceCase(customerId, employeeId, healthSurveillanceCase));
        dispatch(createHealthSurveillanceCaseSuccess(result));
        return result;
    };
};

export const createHealthSurveillanceCaseSuccess = (ohCaseDetails: OhCaseDetailModel): OhCaseAction => {
    return {
        type: CREATE_HEALTH_SURVEILLANCE_CASE_SUCCESS,
        payload: ohCaseDetails
    };
};

const buildLoadHealthSurveillanceQuestionnaireForClinicFromServer =
    (loadQuestionnaireFunc: (state: ApplicationState, clinicId: string, appointmentId: string) =>
    Promise<HealthSurveillanceQuestionnaireAutoSaveDto | null>) =>
        (clinicId: string, appointmentId: string): ThunkAction<Promise<AnsweredQuestionWithId[]>, ApplicationState, undefined, any> =>
            async (dispatch, getState) => {
                const state = getState();
                const questionnaire = await loadQuestionnaireFunc(state, clinicId, appointmentId);
                dispatch(clinicHealthSurveillanceQuestionnaireLoaded({
                    clinicId,
                    appointmentId,
                    questionnaire
                }));

                if (!questionnaire || !questionnaire.data) {
                    return [];
                }

                dispatch(questionnaireLoaded({
                    questionnaireKey: HealthSurveillanceQuestionnaireKey,
                    questions: questionnaire.data.questions,
                    eTag: questionnaire.eTag
                }));

                return questionnaire.data.questions;
            };

const buildLoadHealthSurveillanceRepeatMedicalConsultationForClinicFromServer = (healthSurveillanceApi: HealthSurveillanceApi) => {
    return (clinicId: string, appointmentId: string, taskId: string, abort: AbortController) => {
        return async (dispatch: ThunkDispatch<ApplicationState, undefined, any>) => {
            const response = await loadAndTrack(
                dispatch,
                LOAD_HEALTH_SURVEILLANCE_REPEAT_MEDICAL_CONSULTATION,
                healthSurveillanceApi.loadRepeatMedicalConsultationByTaskId(clinicId, appointmentId, taskId, abort)
            );
            const questions = response?.data?.questions ?? [];
            dispatch(questionnaireLoaded({
                questionnaireKey: HealthSurveillanceRepeatMedicalConsultationKey,
                questions,
                eTag: response?.eTag
            }));
            return questions;
        };
    };
};

export const loadHealthSurveillanceQuestionnaireForClinicFromServer = buildLoadHealthSurveillanceQuestionnaireForClinicFromServer(
    (state, clinicId, appointmentId) => new HealthSurveillanceApi(axios).loadQuestionnaireByAppointmentId(clinicId, appointmentId)
);

export const loadHealthSurveillanceRepeatMedicalConsultationForClinicFromServer =
    buildLoadHealthSurveillanceRepeatMedicalConsultationForClinicFromServer(new HealthSurveillanceApi(axios));

const buildLoadHealthSurveillanceConsultationForClinicFromServer =
    (loadConsultationFunc: (state: ApplicationState, clinicId: string, appointmentId: string, abort: AbortController) =>
    Promise<HealthSurveillanceQuestionnaireAutoSaveDto | null>) =>
        (clinicId: string, appointmentId: string, abort: AbortController): ThunkAction<Promise<AnsweredQuestionWithId[]>, ApplicationState, undefined, any> =>
            async (dispatch, getState) => {
                const questionnaire = await loadConsultationFunc(getState(), clinicId, appointmentId, abort);

                dispatch(questionnaireLoaded({
                    questionnaireKey: HealthSurveillanceConsultationKey,
                    questions: questionnaire?.data?.questions ?? [],
                    eTag: questionnaire?.eTag ?? null
                }));
                return questionnaire?.data?.questions ?? [];
            };

export const loadHealthSurveillanceConsultationForClinicFromServer = buildLoadHealthSurveillanceConsultationForClinicFromServer(
    (state, clinicId, appointmentId, abort) => new HealthSurveillanceApi(axios).loadConsultationByAppointmentId(clinicId, appointmentId, abort)
);

const buildLoadHealthSurveillanceConsultationForTask = (healthSurveillanceApi: HealthSurveillanceApi) => {
    return (ohCaseId: string, taskId: string, questionnaireKey: string, abort: AbortController) => {
        return async (dispatch: ThunkDispatch<ApplicationState, undefined, any>) => {
            const response = await loadAndTrack(
                dispatch,
                LOAD_HEALTH_SURVEILLANCE_CONSULTATION,
                healthSurveillanceApi.loadConsultationByTaskId(ohCaseId, taskId, abort)
            );
            dispatch(questionnaireLoaded({
                questionnaireKey,
                questions: response?.data?.questions ?? [],
                eTag: response?._etag ?? null
            }));

            return response?.data?.questions ?? [];
        };
    };
};

export const loadHealthSurveillanceConsultationForTask = buildLoadHealthSurveillanceConsultationForTask(new HealthSurveillanceApi(axios));

const buildLoadHealthSurveillanceQuestionnaire = (healthSurveillanceApi: HealthSurveillanceApi) => {
    return (ohCaseId: string, taskId: string, questionnaireKey: string) => {
        return async (dispatch: ThunkDispatch<ApplicationState, undefined, any>) => {
            const response = await loadAndTrack(
                dispatch,
                LOAD_HEALTH_SURVEILLANCE_QUESTIONNAIRE,
                healthSurveillanceApi.loadQuestionnaireByTaskId(ohCaseId, taskId)
            );
            dispatch(questionnaireLoaded({
                questionnaireKey,
                questions: response?.data?.questions ?? [],
                eTag: response?._etag
            }));

            return response?.data?.questions ?? [];
        };
    };
};

export const loadHealthSurveillanceQuestionnaire = buildLoadHealthSurveillanceQuestionnaire(new HealthSurveillanceApi(axios));

const buildDeleteDocumentForQuestionnaire = (healthSurveillanceApi: HealthSurveillanceApi) => {
    return (ohCaseId: string, ohCaseDocumentId: string) => {
        return async (dispatch: ThunkDispatch<ApplicationState, undefined, any>) => {
            await loadAndTrack(
                dispatch,
                DELETE_HEALTH_SURVEILLANCE_QUESTIONNAIRE_EVIDENCE,
                healthSurveillanceApi.deleteDocumentForQuestionnaire(ohCaseId, ohCaseDocumentId) // eslint-disable-line deprecation/deprecation
            );
        };
    };
};
/**
 * @deprecated Replaced by deleteEmployeeQuestionnaireEvidence
 */
export const deleteDocumentForQuestionnaire = buildDeleteDocumentForQuestionnaire(new HealthSurveillanceApi(axios));
export const deleteEmployeeQuestionnaireEvidence = (ohCaseId: string, customerId: string, ohCaseDocumentId: string) => {
    return async (dispatch: ThunkDispatch<ApplicationState, undefined, any>) => {
        await loadAndTrack(
            dispatch,
            DELETE_HEALTH_SURVEILLANCE_QUESTIONNAIRE_EVIDENCE,
            new HealthSurveillanceApi(axios).deleteEmployeeQuestionnaireEvidence(ohCaseId, customerId, ohCaseDocumentId)
        );
    };
};

const buildDeleteDocumentForConsultation = (healthSurveillanceApi: HealthSurveillanceApi) => {
    return (ohCaseId: string, ohCaseDocumentId: string) => {
        return async (dispatch: ThunkDispatch<ApplicationState, undefined, any>) => {
            await loadAndTrack(
                dispatch,
                DELETE_HEALTH_SURVEILLANCE_CONSULTATION_EVIDENCE,
                healthSurveillanceApi.deleteDocumentForConsultation(ohCaseId, ohCaseDocumentId) // eslint-disable-line deprecation/deprecation
            );
        };
    };
};
/**
 * @deprecated Replaced by deleteConsultationEvidence
 */
export const deleteDocumentForConsultation = buildDeleteDocumentForConsultation(new HealthSurveillanceApi(axios));
export const deleteConsultationEvidence = (ohCaseId: string, customerId: string, ohCaseDocumentId: string) => {
    return async (dispatch: ThunkDispatch<ApplicationState, undefined, any>) => {
        await loadAndTrack(
            dispatch,
            DELETE_HEALTH_SURVEILLANCE_CONSULTATION_EVIDENCE,
            new HealthSurveillanceApi(axios).deleteConsultationEvidence(ohCaseId, customerId, ohCaseDocumentId)
        );
    };
};

const buildSubmitHealthSurveillanceQuestionnaire = (healthSurveillanceApi: HealthSurveillanceApi) =>
// eslint-disable-next-line max-len
    (clinicId: string, appointmentId: string, questions: AnsweredQuestionWithId[], questionnaireKey: string): ThunkAction<Promise<boolean>, ApplicationState, undefined, any> => {
        return async (dispatch) => {
            const result = await healthSurveillanceApi.submitQuestionnaireForAppointment(clinicId, appointmentId, questions);
            dispatch(submitQuestionnaireSuccess(questionnaireKey));
            dispatch(submitHealthSurveillanceQuestionnaireSuccess({
                clinicId,
                appointmentId,
                questionnaire: {
                    id: result.id,
                    data: { questions }
                }
            }));
            if (result.ohCaseModel) {
                dispatch(loadOhCaseDetailSuccess(result.ohCaseModel));
            }

            return true;
        };
    };
// eslint-disable-next-line max-len
export const submitHealthSurveillanceQuestionnaire = buildSubmitHealthSurveillanceQuestionnaire(new HealthSurveillanceApi(axios));

const buildSubmitNonClinicHealthSurveillanceQuestionnaire = (healthSurveillanceApi: HealthSurveillanceApi) =>
    (ohCaseId: string, taskId: string, questions: AnsweredQuestionWithId[]): ThunkAction<Promise<boolean>, ApplicationState, undefined, any> => {
        return async (dispatch) => {
            const updatedTasks = await loadAndTrack(
                dispatch,
                SUBMIT_QUESTIONNAIRE,
                healthSurveillanceApi.submitQuestionnaireForTask(ohCaseId, taskId, questions)
            );
            dispatch(submitQuestionnaireSuccess(HealthSurveillanceQuestionnaireKey));
            dispatch(actionTaskSuccess({ ohCaseId, tasks: updatedTasks }));

            return true;
        };
    };

export const submitNonClinicHealthSurveillanceQuestionnaire = buildSubmitNonClinicHealthSurveillanceQuestionnaire(new HealthSurveillanceApi(axios));

// eslint-disable-next-line max-len
const buildSubmitHealthSurveillanceConsultation = (submitFunc: (clinicId: string, appointmentId: string, isHavsTierThreeOrFour?: boolean)
    => Promise<SubmitClinicHealthSurveillanceQuestionnaireResponseDto>) =>
// eslint-disable-next-line max-len
    (clinicId: string, appointmentId: string, questionnaireKey: string, isHavsTierThreeOrFour?: boolean): ThunkAction<Promise<boolean>, ApplicationState, undefined, any> => {
        return async (dispatch) => {
            const result = await submitFunc(clinicId, appointmentId, isHavsTierThreeOrFour);
            dispatch(submitQuestionnaireSuccess(questionnaireKey));
            dispatch(submitHealthSurveillanceConsultationSuccess({
                clinicId,
                appointmentId,
            }));

            if (result.ohCaseModel) {
                dispatch(loadOhCaseDetailSuccess(result.ohCaseModel));
            }

            return true;
        };
    };

export const submitHealthSurveillanceConsultationToServer = buildSubmitHealthSurveillanceConsultation(
    (clinicId: string, appointmentId: string, isHavsTierThreeOrFour?: boolean) =>
        new HealthSurveillanceApi(axios).submitConsultationForAppointment(clinicId, appointmentId, isHavsTierThreeOrFour)
);

const buildSubmitHealthSurveillanceRepeatMedicalConsultation = (submitFunc: (
    clinicId: string,
    appointmentId: string,
    taskId: string,
    isHavsTierThreeOrFour?: boolean
    ) => Promise<SubmitClinicHealthSurveillanceQuestionnaireResponseDto>) =>
    (   clinicId: string,
        appointmentId: string,
        taskId: string,
        questionnaireKey: string,
        isHavsTierThreeOrFour?: boolean
    ): ThunkAction<Promise<boolean>, ApplicationState, undefined, any> => {
        return async (dispatch) => {
            const result = await submitFunc(clinicId, appointmentId, taskId, isHavsTierThreeOrFour);
            dispatch(submitQuestionnaireSuccess(questionnaireKey));
            dispatch(submitHealthSurveillanceRepeatConsultationSuccess({
                clinicId,
                appointmentId,
            }));

            if (result.ohCaseModel) {
                dispatch(loadOhCaseDetailSuccess(result.ohCaseModel));
            }

            return true;
        };
    };

export const submitHealthSurveillanceRepeatMedicalConsultationToServer = buildSubmitHealthSurveillanceRepeatMedicalConsultation(
    (clinicId: string, appointmentId: string, taskId: string, isHavsTierThreeOrFour?: boolean) =>
        new HealthSurveillanceApi(axios).submitRepeatMedicalConsultationForAppointment(clinicId, appointmentId, taskId, isHavsTierThreeOrFour)
);

const buildSaveNonClinicHealthSurveillanceConsultation = (healthSurveillanceApi: HealthSurveillanceApi) =>
    (ohCaseId: string, taskId: string, questions: AnsweredQuestionWithId[], eTag: string | null):
        ThunkAction<Promise<string>, ApplicationState, undefined, any> => {
        return async (dispatch) => {
            const result = await healthSurveillanceApi.saveConsultationForTask(ohCaseId, taskId, questions, eTag);
            dispatch(saveQuestionnaireSuccess({ questionnaireKey: HealthSurveillanceConsultationKey, eTag: result.eTag }));
            return result.id;
        };
    };

export const saveNonClinicHealthSurveillanceConsultation = buildSaveNonClinicHealthSurveillanceConsultation(new HealthSurveillanceApi(axios));

const buildSubmitNonClinicHealthSurveillanceConsultation = (healthSurveillanceApi: HealthSurveillanceApi) =>
    (ohCaseId: string, taskId: string, isHavsTierThreeOrFour?: boolean): ThunkAction<Promise<boolean>, ApplicationState, undefined, any> => {
        return async (dispatch) => {
            const updatedTasks = await loadAndTrack(
                dispatch,
                SUBMIT_QUESTIONNAIRE,
                healthSurveillanceApi.submitConsultationForTask(ohCaseId, taskId, isHavsTierThreeOrFour));
            dispatch(submitQuestionnaireSuccess(HealthSurveillanceConsultationKey));
            dispatch(actionTaskSuccess({ ohCaseId, tasks: updatedTasks }));

            return true;
        };
    };

export const submitNonClinicHealthSurveillanceConsultation = buildSubmitNonClinicHealthSurveillanceConsultation(
    new HealthSurveillanceApi(axios)
);

const buildSaveHealthSurveillanceConsultationToServer = (healthSurveillanceApi: HealthSurveillanceApi) =>
    (
        clinicId: string, appointmentId: string, questions: AnsweredQuestionWithId[], eTag: string | null
    ): ThunkAction<Promise<void>, ApplicationState, undefined, any> => {
        return async (dispatch) => {
            const response = await healthSurveillanceApi.saveConsultationForAppointment(clinicId, appointmentId, questions, eTag);
            dispatch(saveQuestionnaireSuccess({ questionnaireKey: HealthSurveillanceConsultationKey, eTag: response.eTag }));
        };
    };

export const saveHealthSurveillanceConsultationToServer = buildSaveHealthSurveillanceConsultationToServer(new HealthSurveillanceApi(axios));

const buildSaveHealthSurveillanceRepeatMedicalConsultationToServer = (healthSurveillanceApi: HealthSurveillanceApi) =>
    // eslint-disable-next-line max-len
    (clinicId: string, appointmentId: string, taskId: string, questions: AnsweredQuestionWithId[], eTag: string | null): ThunkAction<Promise<void>, ApplicationState, undefined, any> => {
        return async (dispatch) => {
            const response = await healthSurveillanceApi.saveRepeatMedicalConsultationForAppointment(clinicId, appointmentId, taskId, questions, eTag);
            dispatch(saveQuestionnaireSuccess({ questionnaireKey: HealthSurveillanceRepeatMedicalConsultationKey, eTag: response.eTag }));
        };
    };

// eslint-disable-next-line max-len
export const saveHealthSurveillanceRepeatMedicalConsultationToServer = buildSaveHealthSurveillanceRepeatMedicalConsultationToServer(new HealthSurveillanceApi(axios));

export const buildGetMedicalTypesForAppointment = (healthSurveillanceApi: HealthSurveillanceApi) =>
    (clinicId: string, appointmentId: string): ThunkAction<Promise<MedicalTypeId[]>, ApplicationState, undefined, any> => () => {
        return healthSurveillanceApi.getMedicalTypesForAppointment(clinicId, appointmentId);
    };

/**
 * @deprecated prefer useMedicalTypesForAppointment hook
 */
export const getMedicalTypesForAppointment = buildGetMedicalTypesForAppointment(new HealthSurveillanceApi(axios));

export const buildGetMedicalTypesForOhCase = (healthSurveillanceApi: HealthSurveillanceApi) =>
    (customerId: string, ohCaseId: string): ThunkAction<Promise<MedicalTypeId[]>, ApplicationState, undefined, any> => () => {
        return healthSurveillanceApi.getMedicalTypesForOhCase(customerId, ohCaseId);
    };

/**
 * @deprecated prefer useMedicalTypesForOhCase hook
 */
export const getMedicalTypesForOhCase = buildGetMedicalTypesForOhCase(new HealthSurveillanceApi(axios));

export const buildSendHealthSurveillanceQuestionnaire = (healthSurveillanceApi: HealthSurveillanceApi) =>
    (customerId: string, ohCaseId: string, sendQuestionnaireTaskId: string, consultationRequired?: boolean) => async (dispatch: Dispatch) => {
        const updatedTasks = await loadAndTrack(
            dispatch,
            SEND_HEALTH_SURVEILLANCE_QUESTIONNAIRE,
            healthSurveillanceApi.sendQuestionnaire(customerId, ohCaseId, sendQuestionnaireTaskId, consultationRequired)
        );
        dispatch(actionTaskSuccess({ ohCaseId, tasks: updatedTasks }));
    };

export const sendHealthSurveillanceQuestionnaire = buildSendHealthSurveillanceQuestionnaire(new HealthSurveillanceApi(axios));

export const completeNowHealthSurveillanceQuestionnaire = (customerId: string, ohCaseId: string, sendQuestionnaireTaskId: string) => {
    return async (dispatch: Dispatch) => {
        const updatedTasks = await loadAndTrack(
            dispatch,
            COMPLETE_NOW_HEALTH_SURVEILLANCE_QUESTIONNAIRE,
            new HealthSurveillanceApi(axios).completeNowHealthSurveillanceQuestionnaire(customerId, ohCaseId, sendQuestionnaireTaskId)
        );
        dispatch(actionTaskSuccess({ ohCaseId, tasks: updatedTasks }));
    };
};

const buildSaveHavsClinicalAssessmentOfHistoryAndExamination = (healthSurveillanceApi: HealthSurveillanceApi) =>
    (ohCaseId: string, taskId: string, questions: AnsweredQuestionWithId[], eTag: string | null):
        ThunkAction<Promise<string>, ApplicationState, undefined, any> => {
        return async (dispatch) => {
            const result = await healthSurveillanceApi.saveHavsClinicalAssessmentOfHistoryAndExamination(ohCaseId, taskId, questions, eTag);
            dispatch(saveQuestionnaireSuccess({ questionnaireKey: HavsClinicalAssessmentOfHistoryAndExaminationKey, eTag: result.eTag }));
            return result.id;
        };
    };

export const saveHavsClinicalAssessmentOfHistoryAndExamination = buildSaveHavsClinicalAssessmentOfHistoryAndExamination(new HealthSurveillanceApi(axios));

const buildSubmitHavsClinicalAssessmentOfHistoryAndExamination = (healthSurveillanceApi: HealthSurveillanceApi) =>
    (ohCaseId: string, taskId: string): ThunkAction<Promise<boolean>, ApplicationState, undefined, any> => {
        return async (dispatch) => {
            const updatedTasks = await loadAndTrack(
                dispatch,
                SUBMIT_QUESTIONNAIRE,
                healthSurveillanceApi.submitHavsClinicalAssessmentOfHistoryAndExamination(ohCaseId, taskId));
            dispatch(submitQuestionnaireSuccess(HavsClinicalAssessmentOfHistoryAndExaminationKey));
            dispatch(actionTaskSuccess({ ohCaseId, tasks: updatedTasks }));

            return true;
        };
    };

export const submitHavsClinicalAssessmentOfHistoryAndExamination = buildSubmitHavsClinicalAssessmentOfHistoryAndExamination(
    new HealthSurveillanceApi(axios)
);

const buildLoadHavsClinicalAssessmentOfHistoryAndExamination = (healthSurveillanceApi: HealthSurveillanceApi) => {
    return (ohCaseId: string, taskId: string, abort: AbortController) => {
        return async (dispatch: ThunkDispatch<ApplicationState, undefined, any>) => {
            const response = await loadAndTrack(
                dispatch,
                LOAD_HEALTH_SURVEILLANCE_CONSULTATION,
                healthSurveillanceApi.loadConsultationByTaskId(ohCaseId, taskId, abort)
            );
            dispatch(questionnaireLoaded({
                questionnaireKey: HavsClinicalAssessmentOfHistoryAndExaminationKey,
                questions: response?.data?.questions ?? [],
                eTag: response?._etag ?? null
            }));
            return response?.data?.questions ?? [];
        };
    };
};

export const loadHavsClinicalAssessmentOfHistoryAndExamination = buildLoadHavsClinicalAssessmentOfHistoryAndExamination(new HealthSurveillanceApi(axios));

const buildActionFurtherInformationAndReviewOfFitness = (healthSurveillanceApi: HealthSurveillanceApi) => {
    return (customerId: string, ohCaseId: string, taskId: string, notes: string) => {
        return async (dispatch: ThunkDispatch<ApplicationState, undefined, any>) => {
            const updatedTasks = await loadAndTrack(
                dispatch,
                ACTION_FURTHER_INFORMATION_AND_REVIEW_OF_FITNESS,
                healthSurveillanceApi.actionFurtherInformationAndReviewOfFitness(customerId, ohCaseId, taskId, notes)
            );
            dispatch(actionTaskSuccess({ ohCaseId, tasks: updatedTasks }));
        };
    };
};

export const actionFurtherInformationAndReviewOfFitness = buildActionFurtherInformationAndReviewOfFitness(new HealthSurveillanceApi(axios));

export const clinicHealthSurveillanceQuestionnaireLoaded = (payload: ClinicHealthSurveillanceQuestionnaire):
    ClinicHealthSurveillanceQuestionnaireLoadedAction => ({
    payload,
    type: LOADED_CLINIC_HEALTH_SURVEILLANCE_QUESTIONNAIRE
});

export const submitHealthSurveillanceQuestionnaireSuccess = (payload: ClinicHealthSurveillanceQuestionnaire):
    SubmitHealthSurveillanceQuestionnaireSuccessAction => ({
    payload,
    type: SUBMIT_CLINIC_HEALTH_SURVEILLANCE_QUESTIONNAIRE_SUCCESS
});

export const submitHealthSurveillanceConsultationSuccess = (payload: { clinicId: string; appointmentId: string }):
    SubmitHealthSurveillanceConsultationSuccessAction => ({
    payload,
    type: SUBMIT_CLINIC_HEALTH_SURVEILLANCE_CONSULTATION_SUCCESS
});

export const submitHealthSurveillanceRepeatConsultationSuccess = (payload: { clinicId: string; appointmentId: string }):
SubmitHealthSurveillanceRepeatConsultationSuccessAction => ({
    payload,
    type: SUBMIT_CLINIC_HEALTH_SURVEILLANCE_REPEAT_CONSULTATION_SUCCESS
});
